import { useState, useEffect, useRef } from 'react'
import { analyze, type AnalysisResult } from '../api/enApi'

// Re-export types
export type { AnalysisResult }

// Custom hook for EN parsing with debounce
// Works in both DEV (local parser) and PROD (server API) modes
export function useENParser(source: string, isDark: boolean = true, view?: string, seedColor?: string, themeName?: string, direction?: string) {
    const [result, setResult] = useState<AnalysisResult | null>(null)
    const [isProcessing, setIsProcessing] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const requestIdRef = useRef(0)

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
        }

        setIsProcessing(true)

        const lineCount = source.split('\n').filter(l => l.trim().startsWith('do:')).length
        const debounceMs = lineCount > 100 ? 2000 : lineCount > 50 ? 800 : 300

        const thisRequestId = ++requestIdRef.current

        debounceRef.current = setTimeout(async () => {
            try {
                const parsed = await analyze(source, isDark, view, seedColor, themeName, direction)
                if (requestIdRef.current !== thisRequestId) return
                setResult(parsed)
                setError(null)
                // Role colors are diagram-specific — apply them.
                // Accent and other theme colors are managed exclusively by ThemeProvider.
                // Do NOT set them on documentElement — it conflicts with ThemeProvider's wrapper div.
                if (parsed.theme) {
                    const root = document.documentElement
                    root.style.setProperty('--role-action', parsed.theme.roleAction)
                    root.style.setProperty('--role-source', parsed.theme.roleSource)
                    root.style.setProperty('--role-result', parsed.theme.roleResult)
                    root.style.setProperty('--role-intermediate', parsed.theme.roleIntermediate)
                    root.style.setProperty('--role-hub', parsed.theme.roleHub)
                    root.style.setProperty('--role-locus', parsed.theme.roleLocus)
                }
            } catch (e) {
                if (requestIdRef.current !== thisRequestId) return
                setError(e instanceof Error ? e.message : 'Unknown error')
                setResult(null)
            } finally {
                if (requestIdRef.current === thisRequestId) {
                    setIsProcessing(false)
                }
            }
        }, debounceMs)

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
        }
    }, [source, isDark, view, seedColor, themeName, direction])

    return { result, isProcessing, error }
}
